import { DutyScheduleColumnResponse, DutyScheduleCounselorOptionResponse, DutyScheduleSlotResponse } from "@/api";
import { userCodesByCounselorId } from "@/pages/duty-schedule/pages/duty-schedule-detail/pages/data-entry/DutyScheduleDataEntry.utils";

// Sentinels rather than a real counselor id: no id can collide with either.
export const UNFILLED_KEY = "__unfilled__";
export const TOTALS_KEY = "__totals__";

export interface DutyScheduleOverviewRow {
  // The counselor id, or one of the sentinels above.
  key: string;
  userCode: string;
  // null for the unfilled/totals row, and for a counselor the slots reference who no longer has a
  // roster entry (somebody deactivated since the schedule was calculated) — rendered by the bare code.
  counselor: DutyScheduleCounselorOptionResponse | null;
  counts: Map<string, number>;
  total: number;
}

function countByCounselorAndColumn(slots: DutyScheduleSlotResponse[]): Map<string | null, Map<string, number>> {
  const byCounselor = new Map<string | null, Map<string, number>>();

  for (const slot of slots) {
    const perColumn = byCounselor.get(slot.counselorId) ?? new Map<string, number>();
    perColumn.set(slot.columnId, (perColumn.get(slot.columnId) ?? 0) + 1);
    byCounselor.set(slot.counselorId, perColumn);
  }

  return byCounselor;
}

function sumCounts(counts: Map<string, number>): number {
  let total = 0;
  for (const count of counts.values()) {
    total += count;
  }
  return total;
}

function toRow(
  key: string,
  userCode: string,
  counselor: DutyScheduleCounselorOptionResponse | null,
  counts: Map<string, number> | undefined
): DutyScheduleOverviewRow {
  const rowCounts = counts ?? new Map<string, number>();
  return { key, userCode, counselor, counts: rowCounts, total: sumCounts(rowCounts) };
}

function sortKey(row: DutyScheduleOverviewRow): string {
  return row.counselor == null ? row.userCode : `${row.counselor.lastName}, ${row.counselor.firstName}`;
}

// One row per counselor in the active roster — even one with no Dienst at all — plus one for any
// counselor the slots reference that the roster no longer has (mirrors the fallback
// DutyScheduleCounselorFilter already uses for somebody who has since left), sorted alphabetically by
// name the way the table displays it.
export function buildCounselorOverviewRows(
  slots: DutyScheduleSlotResponse[],
  counselors: DutyScheduleCounselorOptionResponse[]
): { unfilledRow: DutyScheduleOverviewRow; counselorRows: DutyScheduleOverviewRow[] } {
  const byCounselor = countByCounselorAndColumn(slots);
  const knownCounselorIds = new Set(counselors.map((counselor) => counselor.id));
  const userCodes = userCodesByCounselorId(slots);

  const orphanCounselorIds = [...byCounselor.keys()].filter(
    (counselorId): counselorId is string => counselorId != null && !knownCounselorIds.has(counselorId)
  );

  const counselorRows = [
    ...counselors.map((counselor) => toRow(counselor.id, counselor.userCode, counselor, byCounselor.get(counselor.id))),
    ...orphanCounselorIds.map((counselorId) =>
      toRow(counselorId, userCodes.get(counselorId) ?? "", null, byCounselor.get(counselorId))
    ),
  ].sort((left, right) => sortKey(left).localeCompare(sortKey(right)));

  const unfilledRow = toRow(UNFILLED_KEY, "", null, byCounselor.get(null));

  return { unfilledRow, counselorRows };
}

// Sums whichever rows are currently on screen, per column plus a grand total. That single rule is what
// makes Gesamtergebnis mean "totals of the whole Dienstplan" when unfiltered and "totals of the
// filtered row" when filtered to one Mitarbeiter:in — there is no separate branch for either case.
export function sumOverviewRows(
  columns: DutyScheduleColumnResponse[],
  rows: DutyScheduleOverviewRow[]
): DutyScheduleOverviewRow {
  const counts = new Map<string, number>();

  for (const column of columns) {
    counts.set(
      column.id,
      rows.reduce((sum, row) => sum + (row.counts.get(column.id) ?? 0), 0)
    );
  }

  return toRow(TOTALS_KEY, "", null, counts);
}
